import { DotsMark, RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { useState } from "react";

import { fallbackCoverFor } from "~/lib/fallback-cover";

/**
 * Article artwork, always shown whole at 16:9.
 *
 * Three rules are implemented here so no call site has to remember them:
 *
 * - **Never cropped.** Covers are photographs with the brand's watermark in their
 *   bottom-right corner, generated or chosen as reusable headers. A fixed-height box
 *   would crop them differently at every breakpoint — cutting off the mark, and
 *   often the subject — so the box takes the image's own shape instead.
 * - **The mark is in the photograph.** The API stamps the Dots into every cover it
 *   publishes, so painting them here as well would print the logo twice.
 * - **The fallback.** No cover, or one that fails to load, gets one of the brand
 *   illustrations — with the stamp, since it has none of its own. Which one is
 *   picked from the slug, so an article always looks the same rather than shuffling
 *   between renders.
 */

export function ArticleCover({
  slug,
  src,
  alt,
  sx,
}: {
  slug: string;
  src: string | null;
  alt: string;
  sx?: SxProps<Theme>;
}) {
  // Keyed on the URL that failed, so a new `src` gets its own attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const photo = src && src !== failedSrc ? src : null;

  return (
    <Box
      data-testid={`cover.${photo ? "photo" : "fallback"}`}
      sx={[
        {
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          overflow: "hidden",
          borderRadius: `${RADIUS.lg}px`,
          backgroundColor: "surface.inset",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        component="img"
        src={photo ?? fallbackCoverFor(slug)}
        alt={photo ? alt : ""}
        loading="lazy"
        onError={photo ? () => setFailedSrc(photo) : undefined}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />

      {photo ? null : (
        <Box
          sx={{
            position: "absolute",
            // The same place and size the API stamps the mark into a photograph, so
            // fallback art and photos wear it identically.
            right: "3.5%",
            bottom: "6.2%",
            width: "7%",
            aspectRatio: "1",
            opacity: 0.85,
            display: "flex",
          }}
        >
          <DotsMark size="100%" color="white" />
        </Box>
      )}
    </Box>
  );
}
